// ─── App Routes ────────────────────────────────────────────────────────────────

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import AdminRoute from './AdminRoute';
import GlobalLayout from '../components/layout/GlobalLayout';
import Spinner from '../components/ui/Spinner';

// Lazy-loaded pages
const LoginPage           = lazy(() => import('../pages/auth/LoginPage'));
const ForgotPasswordPage  = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage   = lazy(() => import('../pages/auth/ResetPasswordPage'));
const VerifyTwoFactorPage = lazy(() => import('../pages/auth/VerifyTwoFactorPage'));
const ConfirmEmailPage    = lazy(() => import('../pages/auth/ConfirmEmailPage'));
const ResendConfirmationPage = lazy(() => import('../pages/auth/ResendConfirmationPage'));
const DashboardPage       = lazy(() => import('../pages/dashboard/DashboardPage'));
const DispatchPage        = lazy(() => import('../pages/dispatch/DispatchPage'));
const WelcomePage         = lazy(() => import('../pages/onboarding/WelcomePage'));
const PersonalProfilePage = lazy(() => import('../pages/onboarding/PersonalProfilePage'));
const LicensingPage       = lazy(() => import('../pages/onboarding/LicensingPage'));
const InsurancePage       = lazy(() => import('../pages/onboarding/InsurancePage'));
const MandatoryDocsPage   = lazy(() => import('../pages/onboarding/MandatoryDocsPage'));
const BankAccountPage     = lazy(() => import('../pages/onboarding/BankAccountPage'));
const WorkingHoursPage    = lazy(() => import('../pages/onboarding/WorkingHoursPage'));
const SummaryPage         = lazy(() => import('../pages/onboarding/SummaryPage'));
const SuccessPage         = lazy(() => import('../pages/onboarding/SuccessPage'));
const ResumeOnboardingPage = lazy(() => import('../pages/onboarding/ResumeOnboardingPage'));
const EditOnboardingPage = lazy(() => import('../pages/onboarding/EditOnboardingPage'));
const OnboardingApplicationReviewPage = lazy(() => import('../pages/onboarding/OnboardingApplicationReviewPage'));
const TrainingPage        = lazy(() => import('../pages/training/TrainingPage'));
const DriverOnboardingTrainingPage = lazy(() => import('../pages/onboarding/TrainingPage'));

// PA Onboarding pages
const PAWelcomePage       = lazy(() => import('../pages/pa-onboarding/PAWelcomePage'));
const PAProfilePage       = lazy(() => import('../pages/pa-onboarding/PAProfilePage'));
const PALicensingPage     = lazy(() => import('../pages/pa-onboarding/PALicensingPage'));
const PAMandatoryDocsPage = lazy(() => import('../pages/pa-onboarding/PAMandatoryDocsPage'));
const PAInsurancePage     = lazy(() => import('../pages/pa-onboarding/PAInsurancePage'));
const PABankPage          = lazy(() => import('../pages/pa-onboarding/PABankPage'));
const PATrainingPage      = lazy(() => import('../pages/pa-onboarding/PATrainingPage'));
const PASummaryPage       = lazy(() => import('../pages/pa-onboarding/PASummaryPage'));
const PASuccessPage       = lazy(() => import('../pages/pa-onboarding/PASuccessPage'));
const PAResumeOnboardingPage = lazy(() => import('../pages/pa-onboarding/PAResumeOnboardingPage'));
const MessagesPage        = lazy(() => import('../pages/messages/MessagesPage'));
const BookingsPage        = lazy(() => import('../pages/bookings/BookingsPage'));
const BookingFormPage     = lazy(() => import('../pages/bookings/BookingFormPage'));
const BookingViewPage     = lazy(() => import('../pages/bookings/BookingViewPage'));
const AccountsListPage    = lazy(() => import('../pages/accounts/AccountsListPage'));
const AccountFormPage     = lazy(() => import('../pages/accounts/AccountFormPage'));
const AccountViewPage     = lazy(() => import('../pages/accounts/AccountViewPage'));
const VehiclesListPage    = lazy(() => import('../pages/vehicles/VehiclesListPage'));
const VehicleFormPage     = lazy(() => import('../pages/vehicles/VehicleFormPage'));
const VehicleViewPage     = lazy(() => import('../pages/vehicles/VehicleViewPage'));
const PlacesListPage      = lazy(() => import('../pages/places/PlacesListPage'));
const PlaceFormPage       = lazy(() => import('../pages/places/PlaceFormPage'));
const PlaceViewPage       = lazy(() => import('../pages/places/PlaceViewPage'));
const DriversListPage     = lazy(() => import('../pages/drivers/DriversListPage'));
const DriversOnShiftPage  = lazy(() => import('../pages/shifts/DriversOnShiftPage'));
const ShiftDetailPage     = lazy(() => import('../pages/shifts/ShiftDetailPage'));
const DriverFormPage      = lazy(() => import('../pages/drivers/DriverFormPage'));
const DriverViewPage      = lazy(() => import('../pages/drivers/DriverViewPage'));
const DriverHoursPage     = lazy(() => import('../pages/drivers/DriverHoursPage'));
const PassengersListPage  = lazy(() => import('../pages/passengers/PassengersListPage'));
const PassengerFormPage   = lazy(() => import('../pages/passengers/PassengerFormPage'));
const PassengerViewPage   = lazy(() => import('../pages/passengers/PassengerViewPage'));
const PAsListPage         = lazy(() => import('../pages/pas/PAsListPage'));
const PAFormPage          = lazy(() => import('../pages/pas/PAFormPage'));
const PAViewPage          = lazy(() => import('../pages/pas/PAViewPage'));
const OptionSetsPage      = lazy(() => import('../pages/admin/OptionSetsPage'));
const WorkingHoursPreferencesPage = lazy(() => import('../pages/admin/WorkingHoursPreferencesPage'));
const BulkImportPage      = lazy(() => import('../pages/admin/BulkImportPage'));
const TripStartRulesPage = lazy(() => import('../pages/settings/TripStartRulesPage'));
const SecurityPage        = lazy(() => import('../pages/settings/SecurityPage'));
const EmailLogPage        = lazy(() => import('../pages/settings/EmailLogPage'));
const HolidaysPage        = lazy(() => import('../pages/holidays/HolidaysPage'));
const LicensingCouncilsListPage = lazy(() => import('../pages/licensing-councils/LicensingCouncilsListPage'));
const LicensingCouncilFormPage  = lazy(() => import('../pages/licensing-councils/LicensingCouncilFormPage'));
const LicensingCouncilViewPage  = lazy(() => import('../pages/licensing-councils/LicensingCouncilViewPage'));
const SubsidiariesListPage = lazy(() => import('../pages/subsidiaries/SubsidiariesListPage'));
const SubsidiaryFormPage   = lazy(() => import('../pages/subsidiaries/SubsidiaryFormPage'));
const DriverMonthlyPaymentPage = lazy(() => import('../pages/reports/DriverMonthlyPaymentPage'));

// Legal (public — linked from the onboarding declaration checkbox)
const TermsPage           = lazy(() => import('../pages/legal/TermsPage'));
const PrivacyPolicyPage   = lazy(() => import('../pages/legal/PrivacyPolicyPage'));

const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Spinner size="lg" color="navy" />
  </div>
);

/** Wraps a page in ProtectedRoute + GlobalLayout */
const Protected: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <GlobalLayout>{children}</GlobalLayout>
  </ProtectedRoute>
);

const AppRoutes: React.FC = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/verify-2fa" element={<VerifyTwoFactorPage />} />
      <Route path="/auth/confirm-email" element={<ConfirmEmailPage />} />
      <Route path="/auth/resend-confirmation" element={<ResendConfirmationPage />} />
      <Route path="/legal/terms"   element={<TermsPage />} />
      <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />

      {/* Dashboard */}
      <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/dispatch"  element={<Protected><DispatchPage  /></Protected>} />

      {/* Onboarding */}
      <Route path="/onboarding/welcome"       element={<Protected><WelcomePage /></Protected>} />
      <Route path="/onboarding/personal"      element={<Protected><PersonalProfilePage /></Protected>} />
      <Route path="/onboarding/licensing"     element={<Protected><LicensingPage /></Protected>} />
      <Route path="/onboarding/insurance"     element={<Protected><InsurancePage /></Protected>} />
      <Route path="/onboarding/mandatory"     element={<Protected><MandatoryDocsPage /></Protected>} />
      <Route path="/onboarding/bank"          element={<Protected><BankAccountPage /></Protected>} />
      <Route path="/onboarding/working-hours" element={<Protected><WorkingHoursPage /></Protected>} />
      <Route path="/onboarding/training"      element={<Protected><DriverOnboardingTrainingPage /></Protected>} />
      <Route path="/onboarding/summary"       element={<Protected><SummaryPage /></Protected>} />
      <Route path="/onboarding/success"       element={<Protected><SuccessPage /></Protected>} />
      <Route path="/onboarding/resume/:id"    element={<Protected><ResumeOnboardingPage /></Protected>} />
      <Route path="/onboarding/edit/:id"      element={<Protected><EditOnboardingPage /></Protected>} />
      <Route path="/onboarding/review/:id"    element={<Protected><OnboardingApplicationReviewPage /></Protected>} />

      {/* Training */}
      <Route path="/training" element={<Protected><TrainingPage /></Protected>} />

      {/* PA Onboarding */}
      <Route path="/pa-onboarding/welcome"   element={<Protected><PAWelcomePage /></Protected>} />
      <Route path="/pa-onboarding/profile"   element={<Protected><PAProfilePage /></Protected>} />
      <Route path="/pa-onboarding/licensing" element={<Protected><PALicensingPage /></Protected>} />
      <Route path="/pa-onboarding/mandatory" element={<Protected><PAMandatoryDocsPage /></Protected>} />
      <Route path="/pa-onboarding/insurance" element={<Protected><PAInsurancePage /></Protected>} />
      <Route path="/pa-onboarding/bank"      element={<Protected><PABankPage /></Protected>} />
      <Route path="/pa-onboarding/training"  element={<Protected><PATrainingPage /></Protected>} />
      <Route path="/pa-onboarding/summary"   element={<Protected><PASummaryPage /></Protected>} />
      <Route path="/pa-onboarding/success"   element={<Protected><PASuccessPage /></Protected>} />
      <Route path="/pa-onboarding/resume/:id"  element={<Protected><PAResumeOnboardingPage /></Protected>} />
      <Route path="/pa-onboarding/review/:id"  element={<Protected><OnboardingApplicationReviewPage /></Protected>} />

      {/* Messages */}
      <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />

      {/* Bookings */}
      <Route path="/bookings"          element={<Protected><BookingsPage /></Protected>} />
      <Route path="/bookings/new"      element={<Protected><BookingFormPage /></Protected>} />
      <Route path="/bookings/:id"      element={<Protected><BookingViewPage /></Protected>} />
      <Route path="/bookings/:id/edit" element={<Protected><BookingFormPage /></Protected>} />
      {/* Accounts */}
      <Route path="/accounts"          element={<Protected><AccountsListPage /></Protected>} />
      <Route path="/accounts/new"      element={<Protected><AccountFormPage /></Protected>} />
      <Route path="/accounts/:id"      element={<Protected><AccountViewPage /></Protected>} />
      <Route path="/accounts/:id/edit" element={<Protected><AccountFormPage /></Protected>} />
      {/* Vehicles */}
      <Route path="/vehicles"          element={<Protected><VehiclesListPage /></Protected>} />
      <Route path="/vehicles/new"      element={<Protected><VehicleFormPage /></Protected>} />
      <Route path="/vehicles/:id"      element={<Protected><VehicleViewPage /></Protected>} />
      <Route path="/vehicles/:id/edit" element={<Protected><VehicleFormPage /></Protected>} />
      {/* Places */}
      <Route path="/places"          element={<Protected><PlacesListPage /></Protected>} />
      <Route path="/places/new"      element={<Protected><PlaceFormPage /></Protected>} />
      <Route path="/places/:id"      element={<Protected><PlaceViewPage /></Protected>} />
      <Route path="/places/:id/edit" element={<Protected><PlaceFormPage /></Protected>} />
      {/* Passengers */}
      <Route path="/passengers"          element={<Protected><PassengersListPage /></Protected>} />
      <Route path="/passengers/new"      element={<Protected><PassengerFormPage /></Protected>} />
      <Route path="/passengers/:id"      element={<Protected><PassengerViewPage /></Protected>} />
      <Route path="/passengers/:id/edit" element={<Protected><PassengerFormPage /></Protected>} />
      {/* Drivers */}
      <Route path="/drivers"          element={<Protected><DriversListPage /></Protected>} />
      <Route path="/drivers/on-shift" element={<Protected><DriversOnShiftPage /></Protected>} />
      <Route path="/shifts/:id"       element={<Protected><ShiftDetailPage /></Protected>} />
      <Route path="/drivers/new"      element={<Protected><DriverFormPage /></Protected>} />
      <Route path="/drivers/:id"        element={<Protected><DriverViewPage /></Protected>} />
      <Route path="/drivers/:id/edit"   element={<Protected><DriverFormPage /></Protected>} />
      <Route path="/drivers/:id/hours"  element={<Protected><DriverHoursPage /></Protected>} />
      {/* PAs */}
      <Route path="/pas"          element={<Protected><PAsListPage /></Protected>} />
      <Route path="/pas/new"      element={<Protected><PAFormPage /></Protected>} />
      <Route path="/pas/:id"      element={<Protected><PAViewPage /></Protected>} />
      <Route path="/pas/:id/edit" element={<Protected><PAFormPage /></Protected>} />
      {/* Admin */}
      <Route path="/admin/option-sets" element={<Protected><OptionSetsPage /></Protected>} />
      <Route path="/admin/working-hours-preferences" element={<Protected><WorkingHoursPreferencesPage /></Protected>} />
      <Route path="/admin/bulk-import" element={<Protected><BulkImportPage /></Protected>} />
      {/* Holidays */}
      <Route path="/holidays" element={<Protected><HolidaysPage /></Protected>} />
      {/* Licensing Councils */}
      <Route path="/licensing-councils"          element={<Protected><LicensingCouncilsListPage /></Protected>} />
      <Route path="/licensing-councils/new"      element={<Protected><LicensingCouncilFormPage /></Protected>} />
      <Route path="/licensing-councils/:id"      element={<Protected><LicensingCouncilViewPage /></Protected>} />
      <Route path="/licensing-councils/:id/edit" element={<Protected><LicensingCouncilFormPage /></Protected>} />

      <Route path="/subsidiaries"          element={<Protected><SubsidiariesListPage /></Protected>} />
      <Route path="/subsidiaries/new"      element={<Protected><SubsidiaryFormPage /></Protected>} />
      <Route path="/subsidiaries/:id/edit" element={<Protected><SubsidiaryFormPage /></Protected>} />
      {/* Reports */}
      <Route path="/reports/drivers/monthly-payment" element={<Protected><DriverMonthlyPaymentPage /></Protected>} />
      {/* Settings */}
      <Route path="/settings" element={<Navigate to="/settings/security" replace />} />
      <Route path="/settings/security" element={<Protected><SecurityPage /></Protected>} />
      <Route path="/settings/settings" element={<Protected><TripStartRulesPage /></Protected>} />
      {/* Admin-only — managers are redirected (the API also returns 403 for them) */}
      <Route path="/settings/email-log" element={<Protected><AdminRoute><EmailLogPage /></AdminRoute></Protected>} />
      {/* Fallback */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
